/**
 * oddsWire.js — decodes the server's compact odds rows (messages with fmt: 2).
 *
 * The server sends field names once per message (`cols`), repeated text once
 * (`dict`, which the `dictCols` columns index into) and each row as a value
 * array; an extra trailing array lists the fields a row doesn't have. Decoding
 * gives the same objects the plain format's JSON.parse does.
 *
 * Rows are built by a small generated function per column layout, so every row
 * gets one fixed object shape, like JSON.parse output. Adding the fields one at
 * a time instead left rows ~75% bigger in memory than the plain format
 * (measured); built this way they're ~60% smaller. If generating functions is
 * blocked (a strict Content-Security-Policy), rows are built field by field.
 */

const builders = new Map();   // "cols|dictCols" -> row builder
const MAX_BUILDERS = 200;
let canGenerate = true;

function makeBuilder(cols, dictCols) {
  const dictSet = new Set(dictCols);
  if (canGenerate) {
    try {
      const fields = cols.map((c, i) => (dictSet.has(c)
        ? `${JSON.stringify(c)}: typeof r[${i}] === 'number' ? d[r[${i}]] : r[${i}]`
        : `${JSON.stringify(c)}: r[${i}]`));
      // eslint-disable-next-line no-new-func
      return new Function('r', 'd', `return { ${fields.join(', ')} };`);
    } catch (e) {
      canGenerate = false;
    }
  }
  const isDict = cols.map(c => dictSet.has(c));
  return (r, d) => {
    const o = {};
    for (let i = 0; i < cols.length; i++) {
      o[cols[i]] = isDict[i] && typeof r[i] === 'number' ? d[r[i]] : r[i];
    }
    return o;
  };
}

export function decodeOddsRows(msg) {
  const { cols, dictCols, dict, rows } = msg;
  const key = `${cols.join(',')}|${dictCols.join(',')}`;
  let build = builders.get(key);
  if (!build) {
    if (builders.size >= MAX_BUILDERS) builders.clear();
    build = makeBuilder(cols, dictCols);
    builders.set(key, build);
  }
  const n = cols.length;
  const out = new Array(rows.length);
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const o = build(row, dict);
    if (row.length > n) for (const c of row[n]) delete o[cols[c]];
    out[i] = o;
  }
  return out;
}
